import {useTranslation} from 'react-i18next'
import {Autocomplete, TextField, createFilterOptions} from '@mui/material'

interface TagAutocompleteProps {
  value: string[]
  onChange: (next: string[]) => void
  suggestions: string[]
  allowCreate: boolean
  placeholder?: string
  ariaLabel?: string
  maxTags?: number
  maxLength?: number
  maxVisibleSuggestions?: number
  onValidationError?: (kind: 'too-long' | 'too-many') => void
}

const DEFAULT_MAX_TAGS = 10
const DEFAULT_MAX_LENGTH = 30
const DEFAULT_MAX_VISIBLE = 5
const CREATE_PREFIX = '__create__:'

const TagAutocomplete = ({
  value,
  onChange,
  suggestions,
  allowCreate,
  placeholder,
  ariaLabel,
  maxTags = DEFAULT_MAX_TAGS,
  maxLength = DEFAULT_MAX_LENGTH,
  maxVisibleSuggestions = DEFAULT_MAX_VISIBLE,
  onValidationError,
}: TagAutocompleteProps) => {
  const {t} = useTranslation()

  // Always-bounded filter; injects a synthetic "Create 'foo'" entry when allowCreate.
  const filter = createFilterOptions<string>({matchFrom: 'any', limit: maxVisibleSuggestions})

  const filterOptions = (options: string[], params: {inputValue: string; getOptionLabel: (o: string) => string}) => {
    const filtered = filter(options, params)
    const input = params.inputValue.trim().toLowerCase()
    if (!allowCreate || !input) return filtered
    if (value.length >= maxTags) return filtered
    const isExisting = options.some((o) => o.toLowerCase() === input)
    const isSelected = value.some((v) => v.toLowerCase() === input)
    if (!isExisting && !isSelected) filtered.push(`${CREATE_PREFIX}${input}`)
    return filtered
  }

  const getOptionLabel = (option: string): string =>
    option.startsWith(CREATE_PREFIX) ? t('tagAutocomplete.create', {value: option.slice(CREATE_PREFIX.length)}) : option

  const handleChange = (_: unknown, raw: string[]) => {
    const seen = new Set<string>()
    const next: string[] = []
    for (const item of raw) {
      const cleaned = item.startsWith(CREATE_PREFIX) ? item.slice(CREATE_PREFIX.length) : item
      const tag = cleaned.trim().toLowerCase()
      if (!tag) continue
      if (tag.length > maxLength) {
        onValidationError?.('too-long')
        continue
      }
      if (seen.has(tag)) continue
      seen.add(tag)
      next.push(tag)
    }
    if (next.length > maxTags) {
      onValidationError?.('too-many')
      onChange(next.slice(0, maxTags))
      return
    }
    onChange(next)
  }

  return (
    <Autocomplete
      multiple
      freeSolo={allowCreate}
      disableCloseOnSelect
      options={suggestions}
      value={value}
      onChange={handleChange}
      filterOptions={filterOptions}
      getOptionLabel={getOptionLabel}
      noOptionsText={t('tagAutocomplete.empty')}
      slotProps={{
        paper: {
          sx: {
            background: 'rgba(14, 11, 26, 0.96)',
            color: '#F5F3FF',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            backdropFilter: 'blur(12px)',
          },
        },
        listbox: {
          sx: {
            '& .MuiAutocomplete-option': {fontSize: 13},
            '& .MuiAutocomplete-option[aria-selected="true"]': {background: 'rgba(124, 92, 255, 0.18)'},
            '& .MuiAutocomplete-option.Mui-focused': {background: 'rgba(255, 255, 255, 0.06)'},
          },
        },
      }}
      sx={{
        flex: 1,
        minWidth: 220,
        '& .MuiOutlinedInput-root': {
          background: 'rgba(20, 17, 32, 0.55)',
          color: '#F5F3FF',
          fontSize: 12.5,
          borderRadius: '9px',
          padding: '3px 8px',
          '& fieldset': {borderColor: 'rgba(255, 255, 255, 0.08)'},
          '&:hover fieldset': {borderColor: 'rgba(255, 255, 255, 0.18)'},
          '&.Mui-focused fieldset': {borderColor: 'rgba(124, 92, 255, 0.45)'},
        },
        '& .MuiAutocomplete-tag': {
          height: 22,
          margin: '2px 4px 2px 0',
          background: 'rgba(124, 92, 255, 0.18)',
          color: '#C4B5FD',
          border: '1px solid rgba(124, 92, 255, 0.35)',
          fontSize: 11,
          fontWeight: 500,
          '& .MuiChip-deleteIcon': {color: '#C4B5FD', fontSize: 14},
          '& .MuiChip-deleteIcon:hover': {color: '#F5F3FF'},
        },
        '& .MuiAutocomplete-clearIndicator, & .MuiAutocomplete-popupIndicator': {color: '#B8B3CC'},
      }}
      renderInput={(params) => {
        const {InputProps, inputProps: paramsInputProps, ...rest} = params
        return (
          <TextField
            {...rest}
            placeholder={value.length === 0 ? placeholder : undefined}
            slotProps={{
              input: InputProps,
              htmlInput: {...paramsInputProps, 'aria-label': ariaLabel, maxLength},
            }}
          />
        )
      }}
    />
  )
}

export default TagAutocomplete
